import React, { useState, useCallback, useEffect, useContext } from 'react'
import { DialogContent, Dialog, Button, IconButton, DialogContentText } from '@mui/material'
import { Info } from '@mui/icons-material';
import styled from 'styled-components'
import theme from 'styles/theme'
import useApi from 'common/hooks/useApi'
import { LineChart, Line, YAxis, Tooltip } from 'recharts'
import { LayoutContext } from 'common/ducks/index'

export default function WorkoutAnalytics({ open, onOpenChange }) {

  const [workoutLoad, setWorkoutLoad] = useState(0)
  const [acwr, setACWR] = useState(0)
  const [acwrDistance, setACWRDistance] = useState(0)
  const [openDialog, setOpenDialog] = useState(false)
  const [showAcwrInfo, setShowAcwrInfo] = useState(false)

  const { setAppLoading } = useContext(LayoutContext)

  const [{ loading: loadingWorkoutLoad}, dataWorkoutLoad ] = useApi({url: 'metrics/workout_load/'}, {manual: true})

  const fetchData = useCallback(
    async () => {
      const response = await dataWorkoutLoad()
      setWorkoutLoad(response?.data?.workoutLoad)
      setACWR(response?.data?.acwr?.strength)
      setACWRDistance(response?.data?.acwr?.distance)
    },
    [dataWorkoutLoad]
  )

  useEffect(() => {
    if (open) {
      setOpenDialog(true)
      fetchData()
    }
  }, [open])

  useEffect(() => {
      setAppLoading(loadingWorkoutLoad )
    }, [loadingWorkoutLoad, setAppLoading])

  const CustomTooltip = function ({ active, payload }) {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip" style={{backgroundColor: '#dedede'}}>
        <p className="label">{payload[0].payload.exercise}</p>
        <p className="intro">{`Strength Load: ${payload[0].value}`}</p>
        <p className="intro">{`Distance Load: ${payload[1].value}`}</p>
      </div>
    );
  }
    return null;
  }

  const handleClose = () => {
    setOpenDialog(false)
    onOpenChange(false)
  }

  let acwrColor = 'black'
  if (acwr < 0.8 && acwr > 0) {
  acwrColor = '#FE9500'
  } else if (acwr >= 0.8 && acwr <= 1.3) {
  acwrColor = '#28a745'
  } else if (acwr > 1.3 && acwr < 1.5) {
  acwrColor = '#FE9500'
  } else if (acwr >= 1.5) {
  acwrColor = '#dc3545'
  } else {
  acwrColor = 'black'
  }

  let acwrMileageColor = 'black'
  if (acwrDistance < 0.8 && acwrDistance > 0) {
  acwrMileageColor = '#FE9500'
  } else if (acwrDistance >= 0.8 && acwrDistance <= 1.3) {
  acwrMileageColor = '#28a745'
  } else if (acwrDistance > 1.3 && acwrDistance < 1.5) {
  acwrMileageColor = '#FE9500'
  } else if (acwrDistance >= 1.5) {
  acwrMileageColor = '#dc3545'
  } else {
  acwrMileageColor = 'black'
  }

  return (
    <Dialog.Stylesheet
      open={openDialog}
      disableEnforceFocus
    >
      <WorkoutAnalytics.Content>
        <div className="header">
          <h2>Workout Analytics</h2>
        </div>

        <div className="content">
          <div className='ACWR'>
            <h3> ACWR </h3>
            <IconButton
              aria-label="Learn More"
              onClick={() => setShowAcwrInfo(true)}
              style={{ paddingTop: 0 }}
            >
              <Info />
            </IconButton>
          </div>
          <div className="card-body" style={{display: 'flex'}}>
              <h4 className="card-title" style={{width: '30%'}}>
                  Strength:
              </h4>
              <h4 style={{ color: acwrColor, width: '20%'}} className="card-title">
                {acwr || '-'}
              </h4>
              <h4 className="card-title" style={{width: '30%'}}>
                Distance:
              </h4>
              <h4 style={{ color: acwrMileageColor, width: '20%' }} className="card-title">
                {acwrDistance || '-'}
              </h4>
          </div>
          <h3> Load Chart </h3>
          <LineChart
            width={280}
            height={200}
            data={workoutLoad}
            margin={{ top: 10, right: 10, bottom: 10, left: 0 }}
          >
            <Tooltip content={<CustomTooltip />} />
            <Line type="monotone" dataKey="load_strength" stroke="#0B545E" />
            <Line type="monotone" dataKey="load_distance" stroke="#36454F" />
            <YAxis width={47} />
          </LineChart>

        </div>

        <div className="footer">
          <Button variant="outlined" style={{ color: '#0000008A' }} onClick={handleClose}>
              Close
          </Button>
        </div>

        <Dialog onClose={() => setShowAcwrInfo(false)} open={showAcwrInfo}>
          <DialogContent>
            <DialogContentText>
              <strong style={{ fontWeight: 'bolder' }}>
                Acute-chronic workload ratio&nbsp;
              </strong>
              is a calculation of an athlete’s fitness and fatigue based on work
              done (ex. weight lifted, distance ran). The acute workload (most
              recent 7 days) is divided by the chronic workload (most recent 28
              days) to present a ratio. The ideal ratio is 0.8-1.3 skewing to
              either side increases fatigue and injury risk due to under/over
              training. The acute-chronic workload can help with understanding the
              preparedness of an athlete, their fatigue level and the relative
              injury risk of an athlete day-to-day.
            </DialogContentText>
          </DialogContent>
        </Dialog>

      </WorkoutAnalytics.Content>
    </Dialog.Stylesheet>
  )
}

Dialog.Stylesheet = styled(Dialog)``

WorkoutAnalytics.Content = styled(DialogContent)`
  padding: 1.5rem !important;
  max-width: 50rem;
  margin: 0 auto;
  .header {
    h2 {
      font-weight: 500;
      font-size: 2rem;
    }
  }
  .content {
    margin-top: 1rem;
    h3 {
      color: ${theme.palette.secondary.g800};
      font-size: 1.25rem;
      font-weight: 500;
    }
    .ACWR {
      display: flex;
      IconButton {
       padding-top: 0px;
      }
    }
  }
  .footer {
    margin-top: 3rem;
    display: flex;
    justify-content: flex-end;

  }
`

WorkoutAnalytics.Stylesheet = styled.div`
  .MuiToolbar-root {
    justify-content: flex-start;
  }
`
